import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export type Lang = 'en' | 'ar';
const KEY = 'mm_site_lang';

@Injectable({ providedIn: 'root' })
export class LanguageService {
  private _lang$ = new BehaviorSubject<Lang>(this.read());
  /** Emits the active language and again whenever it changes. */
  readonly lang$ = this._lang$.asObservable();

  constructor() { this.apply(this._lang$.value); }

  get current(): Lang { return this._lang$.value; }
  get isArabic(): boolean { return this._lang$.value === 'ar'; }

  set(lang: Lang): void {
    if (lang === this._lang$.value) return;
    localStorage.setItem(KEY, lang);
    this.apply(lang);          // flips <html dir/lang> immediately
    this._lang$.next(lang);    // updates all bindings; app.component re-inits the theme (carousels/RTL) live
    // NOTE: deliberately NO location.reload() — a reload does a hard GET to the current
    // deep route, which blanks the page if the host's SPA fallback (web.config) isn't
    // serving index.html for that route. The live re-init in AppComponent handles RTL.
  }

  toggle(): void { this.set(this._lang$.value === 'en' ? 'ar' : 'en'); }

  private read(): Lang {
    const v = (typeof localStorage !== 'undefined' && localStorage.getItem(KEY)) as Lang | null;
    return v === 'ar' ? 'ar' : 'en';
  }

  private apply(lang: Lang): void {
    if (typeof document === 'undefined') return;
    const html = document.documentElement;
    html.setAttribute('lang', lang);
    html.setAttribute('dir', lang === 'ar' ? 'rtl' : 'ltr');
  }
}
